import { useState, useEffect } from 'react';
import { X, Truck } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * VerzendlabelModal — 1-klik verzendlabel via Sendcloud.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - bronType: 'leverbon' | 'kassa' | 'factuur' | 'manueel'
 *  - bronId: string (id van bron-document)
 *  - initial: optionele initiele waarden { name, company, street, house_number, postal_code, city, country, email, telephone, weight, reference }
 *  - onSuccess: (parcel) => void  -- wanneer label is aangemaakt
 */
export default function VerzendlabelModal({ open, onClose, bronType = 'manueel', bronId = '', initial = {}, onSuccess }) {
  const [provider, setProvider] = useState(() => localStorage.getItem('verzend-provider') || 'sendcloud');
  const [recipient, setRecipient] = useState({});
  const [weight, setWeight] = useState(1.0);
  const [methods, setMethods] = useState([]);
  const [methodId, setMethodId] = useState(0);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // { parcel_id, tracking_number, ... }

  useEffect(() => {
    if (open) {
      setRecipient({
        name: initial.name || '',
        company: initial.company || '',
        street: initial.street || '',
        house_number: initial.house_number || '',
        postal_code: initial.postal_code || '',
        city: initial.city || '',
        country: (initial.country || 'BE').toUpperCase(),
        email: initial.email || '',
        telephone: initial.telephone || '',
      });
      setWeight(parseFloat(initial.weight) || 1.0);
      setMethods([]);
      setMethodId(0);
      setCreated(null);
    }
  }, [open, initial.name, initial.postal_code, initial.weight]);

  const ld = (k) => (e) => setRecipient(p => ({ ...p, [k]: e.target.value }));

  const [autoVergelijking, setAutoVergelijking] = useState(null); // {winner, sendcloud, myparcel}

  const fetchMethods = async () => {
    if (!recipient.country) return;
    setLoadingMethods(true);
    setAutoVergelijking(null);
    try {
      if (provider === 'auto') {
        // Auto-modus: vergelijk beide providers
        const r = await API.get('/verzending/best-tarief', {
          params: { weight, to_country: recipient.country },
        });
        setAutoVergelijking(r.data);
        if (!r.data.winner) {
          toast.error('Geen tarieven gevonden bij beide providers', {
            description: 'Configureer minstens één provider in Instellingen → Integraties.',
          });
          setMethods([]);
          setMethodId(0);
        } else {
          // Toon enkel de winnaar als optie + andere kandidaten van dezelfde provider
          const winnerProvider = r.data.winner.provider;
          setMethods([
            { id: r.data.winner.method_id, name: r.data.winner.name, carrier: r.data.winner.carrier, price: r.data.winner.price, min_weight: 0, max_weight: 30, _autoWinner: true, _autoProvider: winnerProvider },
          ]);
          setMethodId(r.data.winner.method_id);
        }
      } else {
        const endpoint = provider === 'myparcel' ? '/myparcel/services' : '/sendcloud/methods';
        const r = await API.get(endpoint, {
          params: { weight, to_country: recipient.country },
        });
        setMethods(r.data.methods || []);
        if ((r.data.methods || []).length > 0) {
          const sorted = [...r.data.methods].sort((a, b) => (a.price || 9999) - (b.price || 9999));
          setMethodId(sorted[0].id);
        }
      }
    } catch (e) {
      toast.error('Kan verzendmethodes niet laden', {
        description: e.response?.data?.detail || e.message,
      });
    }
    setLoadingMethods(false);
  };

  const verstuur = async () => {
    if (!methodId) { toast.error('Kies een verzendmethode'); return; }
    if (!recipient.name || !recipient.street || !recipient.postal_code || !recipient.city) {
      toast.error('Vul minstens naam, straat, postcode en stad in');
      return;
    }
    setBusy(true);
    try {
      // Bepaal effectieve provider: in auto-modus gebruik provider van de gekozen method
      let actualProvider = provider;
      if (provider === 'auto') {
        const m = methods.find(x => x.id === methodId);
        actualProvider = m?._autoProvider || autoVergelijking?.winner?.provider || 'sendcloud';
      }
      const endpoint = actualProvider === 'myparcel' ? '/myparcel/parcel' : '/sendcloud/parcel';
      const r = await API.post(endpoint, {
        bron_type: bronType,
        bron_id: bronId,
        recipient,
        weight,
        shipping_method_id: methodId,
        reference: bronId,
      });
      setCreated({ ...r.data, _provider: actualProvider });
      try { localStorage.setItem('verzend-provider', provider); } catch (_) {}
      toast.success(`Label aangemaakt via ${actualProvider} — ${r.data.tracking_number || r.data.parcel_id}`);
      onSuccess?.(r.data);
    } catch (e) {
      toast.error('Label aanmaken mislukt', {
        description: e.response?.data?.detail || e.message,
      });
    }
    setBusy(false);
  };

  const downloadLabel = async () => {
    if (!created?.parcel_id) return;
    try {
      const prov = created._provider || provider;
      const endpoint = prov === 'myparcel'
        ? `/myparcel/parcel/${created.parcel_id}/label`
        : `/sendcloud/parcel/${created.parcel_id}/label`;
      const r = await API.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Label downloaden mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
      data-testid="verzendlabel-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b-2 border-orange-200 bg-orange-50 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📦</span>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600">Sendcloud verzendlabel</div>
              <div className="text-base font-bold text-gray-900">
                {bronType === 'leverbon' && `Leverbon ${bronId}`}
                {bronType === 'kassa' && `Kassa-ticket ${bronId}`}
                {bronType === 'factuur' && `Factuur ${bronId}`}
                {bronType === 'manueel' && 'Nieuwe verzending'}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="text-gray-400 hover:text-gray-700 p-1 rounded hover:bg-white disabled:opacity-50"
            title="Sluit"
            data-testid="verzendlabel-sluit"
          ><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {/* Stap 0: Verzendprovider kiezen */}
          <div>
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-gray-600 mb-2">Verzendprovider</h4>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => { if (!busy) { setProvider('auto'); setMethods([]); setMethodId(0); } }}
                className={`px-2 py-2 rounded border-2 text-xs font-bold transition flex flex-col items-center justify-center gap-0.5 ${
                  provider === 'auto'
                    ? 'bg-gradient-to-br from-purple-600 to-blue-600 border-purple-700 text-white shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300'
                }`}
                data-testid="vz-provider-auto"
                title="Vergelijkt automatisch beide providers en kiest het goedkoopste tarief"
              >
                <span>🤖 Auto</span>
                <span className="text-[9px] opacity-80 normal-case font-normal">Goedkoopste tarief</span>
              </button>
              <button
                type="button"
                onClick={() => { if (!busy) { setProvider('sendcloud'); setMethods([]); setMethodId(0); } }}
                className={`px-2 py-2 rounded border-2 text-xs font-bold transition flex flex-col items-center justify-center gap-0.5 ${
                  provider === 'sendcloud'
                    ? 'bg-[#FF6B0D] border-[#E55A00] text-white shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300'
                }`}
                data-testid="vz-provider-sendcloud"
              >
                <span>📦 Sendcloud</span>
                <span className="text-[9px] opacity-80 normal-case font-normal">DPD/bpost/PostNL</span>
              </button>
              <button
                type="button"
                onClick={() => { if (!busy) { setProvider('myparcel'); setMethods([]); setMethodId(0); } }}
                className={`px-2 py-2 rounded border-2 text-xs font-bold transition flex flex-col items-center justify-center gap-0.5 ${
                  provider === 'myparcel'
                    ? 'bg-[#FFCB05] border-[#E8B800] text-[#0E1116] shadow-sm'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-yellow-300'
                }`}
                data-testid="vz-provider-myparcel"
              >
                <span>📮 MyParcel</span>
                <span className="text-[9px] opacity-80 normal-case font-normal">PostNL/bpost/DPD</span>
              </button>
            </div>
            <div className="text-[10px] text-gray-500 mt-1.5 italic">
              {provider === 'auto'
                ? '🤖 We vergelijken Sendcloud + MyParcel en kiezen de goedkoopste optie voor je. Je kan altijd nog handmatig een andere methode kiezen.'
                : 'Keuze wordt onthouden voor volgende verzending.'}
            </div>
          </div>

          {/* Stap 1: Ontvanger-adres */}
          <div>
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-gray-600 mb-2">1. Ontvanger</h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><label className="form-label">Naam *</label><input className="field-input" value={recipient.name || ''} onChange={ld('name')} placeholder="Jan Janssens" data-testid="vz-naam" /></div>
              <div className="col-span-2"><label className="form-label">Bedrijf</label><input className="field-input" value={recipient.company || ''} onChange={ld('company')} placeholder="Optioneel" data-testid="vz-bedrijf" /></div>
              <div><label className="form-label">Straat *</label><input className="field-input" value={recipient.street || ''} onChange={ld('street')} placeholder="Hoofdstraat" data-testid="vz-straat" /></div>
              <div><label className="form-label">Nr</label><input className="field-input" value={recipient.house_number || ''} onChange={ld('house_number')} placeholder="42" data-testid="vz-nr" /></div>
              <div><label className="form-label">Postcode *</label><input className="field-input font-mono" value={recipient.postal_code || ''} onChange={ld('postal_code')} placeholder="1000" data-testid="vz-postcode" /></div>
              <div><label className="form-label">Stad *</label><input className="field-input" value={recipient.city || ''} onChange={ld('city')} placeholder="Brussel" data-testid="vz-stad" /></div>
              <div><label className="form-label">Land *</label>
                <select className="field-input" value={recipient.country || 'BE'} onChange={ld('country')} data-testid="vz-land">
                  <option value="BE">België</option>
                  <option value="NL">Nederland</option>
                  <option value="LU">Luxemburg</option>
                  <option value="FR">Frankrijk</option>
                  <option value="DE">Duitsland</option>
                  <option value="GB">Verenigd Koninkrijk</option>
                  <option value="ES">Spanje</option>
                  <option value="IT">Italië</option>
                </select>
              </div>
              <div><label className="form-label">Telefoon</label><input className="field-input" value={recipient.telephone || ''} onChange={ld('telephone')} placeholder="+32 4xx xx xx xx" data-testid="vz-tel" /></div>
              <div className="col-span-2"><label className="form-label">E-mail</label><input type="email" className="field-input" value={recipient.email || ''} onChange={ld('email')} placeholder="klant@email.be" data-testid="vz-email" /></div>
            </div>
          </div>

          {/* Stap 2: Pakket-info */}
          <div>
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-gray-600 mb-2">2. Gewicht & verzendmethode</h4>
            <div className="flex items-end gap-2 mb-2">
              <div className="flex-1">
                <label className="form-label">Gewicht (kg) *</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  className="field-input font-mono"
                  value={weight}
                  onChange={(e) => setWeight(parseFloat(e.target.value) || 1.0)}
                  data-testid="vz-gewicht"
                />
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm shrink-0"
                onClick={fetchMethods}
                disabled={loadingMethods || !recipient.country}
                data-testid="vz-load-methods"
              >
                {loadingMethods ? '⏳' : '🔍'} Toon methodes
              </button>
            </div>
            {provider === 'auto' && autoVergelijking && (
              <div className="mb-2 p-2.5 rounded border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50" data-testid="vz-auto-vergelijking">
                <div className="text-[11px] font-bold text-purple-900 mb-1.5 inline-flex items-center gap-1">
                  🤖 Vergelijking — goedkoopste tarief gekozen
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div className={`p-1.5 rounded border ${autoVergelijking.winner?.provider === 'sendcloud' ? 'bg-orange-100 border-orange-300' : 'bg-white border-gray-200 opacity-70'}`}>
                    <div className="font-bold text-gray-900">📦 Sendcloud {autoVergelijking.winner?.provider === 'sendcloud' && <span className="text-green-700">✓</span>}</div>
                    {autoVergelijking.sendcloud.cheapest ? (
                      <div className="text-[10px] text-gray-700">
                        <div className="truncate">{autoVergelijking.sendcloud.cheapest.name}</div>
                        <div className="font-mono font-bold">€ {autoVergelijking.sendcloud.cheapest.price.toFixed(2)}</div>
                      </div>
                    ) : (
                      <div className="text-[10px] text-gray-400 italic">{autoVergelijking.sendcloud.error || 'Geen tarief'}</div>
                    )}
                  </div>
                  <div className={`p-1.5 rounded border ${autoVergelijking.winner?.provider === 'myparcel' ? 'bg-yellow-100 border-yellow-300' : 'bg-white border-gray-200 opacity-70'}`}>
                    <div className="font-bold text-gray-900">📮 MyParcel {autoVergelijking.winner?.provider === 'myparcel' && <span className="text-green-700">✓</span>}</div>
                    {autoVergelijking.myparcel.cheapest ? (
                      <div className="text-[10px] text-gray-700">
                        <div className="truncate">{autoVergelijking.myparcel.cheapest.name}</div>
                        <div className="font-mono font-bold">€ {autoVergelijking.myparcel.cheapest.price.toFixed(2)}</div>
                      </div>
                    ) : (
                      <div className="text-[10px] text-gray-400 italic">{autoVergelijking.myparcel.error || 'Geen tarief'}</div>
                    )}
                  </div>
                </div>
                {autoVergelijking.sendcloud.cheapest && autoVergelijking.myparcel.cheapest && (() => {
                  const verschil = Math.abs(autoVergelijking.sendcloud.cheapest.price - autoVergelijking.myparcel.cheapest.price);
                  return (
                    <div className="text-[10px] text-green-800 mt-1.5 font-semibold">
                      💰 Besparing: € {verschil.toFixed(2)} per pakket
                    </div>
                  );
                })()}
              </div>
            )}
            {methods.length > 0 && (
              <div className="border border-gray-200 rounded max-h-56 overflow-y-auto" data-testid="vz-methodes">
                {methods.map(m => (
                  <label
                    key={m.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-orange-50 ${methodId === m.id ? 'bg-orange-100' : ''}`}
                  >
                    <input
                      type="radio"
                      name="vz-method"
                      value={m.id}
                      checked={methodId === m.id}
                      onChange={() => setMethodId(m.id)}
                      data-testid={`vz-method-${m.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">{m.name}</div>
                      <div className="text-[10px] uppercase text-gray-500">{m.carrier} · {m.min_weight}–{m.max_weight} kg</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-mono font-bold">{m.price !== null ? `€ ${parseFloat(m.price).toFixed(2)}` : '—'}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {methods.length === 0 && !loadingMethods && (
              <div className="text-[11px] text-gray-500 italic">Vul ontvanger + gewicht in en klik <b>"Toon methodes"</b> om beschikbare verzendopties op te halen.</div>
            )}
          </div>

          {/* Stap 3: Resultaat */}
          {created && (
            <div className="border-2 border-green-300 bg-green-50 rounded p-3" data-testid="vz-result">
              <div className="text-sm font-bold text-green-900 mb-2">✓ Label aangemaakt!</div>
              <div className="text-xs space-y-1 mb-3">
                <div><b>Tracking:</b> <span className="font-mono">{created.tracking_number}</span></div>
                {created.tracking_url && <div><b>Track & trace:</b> <a href={created.tracking_url} target="_blank" rel="noreferrer" className="text-[#002FA7] underline truncate block">{created.tracking_url}</a></div>}
                <div><b>Vervoerder:</b> {created.carrier || '-'}</div>
              </div>
              <button
                onClick={downloadLabel}
                className="btn btn-primary btn-sm inline-flex items-center gap-2"
                data-testid="vz-download-label"
              >
                <Truck size={14} /> Download label PDF
              </button>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          {!created && (
            <button
              onClick={verstuur}
              disabled={busy || !methodId}
              className="btn btn-sm bg-[#FF6B0D] text-white hover:bg-[#E55A00] disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="vz-verstuur"
            >
              {busy ? '⏳ Bezig...' : '📦 Maak label aan'}
            </button>
          )}
          <button
            onClick={onClose}
            disabled={busy}
            className="btn btn-sm btn-ghost disabled:opacity-50"
            data-testid="vz-close-btn"
          >{created ? 'Klaar' : 'Annuleer'}</button>
        </div>
      </div>
    </div>
  );
}
